import { tx } from '@/i18n/pack';

export type TargetingUrlRule = string | {
  url?: string;
  completeMatching?: boolean;
};

export interface TargetingPreviewData {
  urls?: TargetingUrlRule[];
  urlParts?: TargetingUrlRule[];
  excludeUrls?: TargetingUrlRule[];
  excludeUrlParts?: TargetingUrlRule[];
  pages?: Array<string | number>;
  excludePages?: Array<string | number>;
}

export interface TargetingPreviewResult {
  url: string;
  visible: boolean;
  reason: string;
  matchedRule: string;
  pageName?: string;
}

export interface TargetingPreviewExampleRow {
  pageName: string;
  url: string;
  displayUrl?: string;
  visible: boolean;
  reason: string;
  matchedRule: string;
  showStatus: '展示' | '不展示' | '部分展示';
  details: TargetingPreviewResult[];
}

interface ParsedUrl {
  hostname: string;
  pathname: string;
  parameters: string;
}

interface PageRuleGroup {
  exactUrls: TargetingUrlRule[];
  includeUrls: TargetingUrlRule[];
}

const PAGE_NAME_KEYS: Record<string, [string, string]> = {
  '1': ['form.rules.page.home', '首页'],
  '2': ['form.rules.page.list', '列表页'],
  '3': ['form.rules.page.detail', '商品详情页'],
  '4': ['form.rules.page.cart', '购物车'],
  '5': ['form.rules.page.checkout', '结账页'],
};

function pageName(id: string) {
  const hit = PAGE_NAME_KEYS[id];
  return hit ? tx(null, hit[0], hit[1]) : id;
}

function normalizePageIds(pages?: Array<string | number>): string[] {
  return (pages || []).map((page) => String(page));
}

// 与 C 端 logic.ts 的 replaceAll 行为保持一致：移除所有出现的协议前缀和 www.，
// 而非仅移除开头（C 端使用 replaceAll('www.', '') 会移除 URL 中所有的 www.）
function normalizeHost(hostname: string): string {
  let result = hostname || '';
  result = replaceAllText(result, 'https://', '');
  result = replaceAllText(result, 'http://', '');
  result = replaceAllText(result, 'www.', '');
  return result;
}

function normalizeRuleUrl(url: string): string {
  let result = url || '';
  result = replaceAllText(result, 'https://', '');
  result = replaceAllText(result, 'http://', '');
  result = replaceAllText(result, 'www.', '');
  return result;
}

function replaceAllText(value: string, searchValue: string, replaceValue: string): string {
  return value.split(searchValue).join(replaceValue);
}

function parsePreviewUrl(inputUrl: string, fallbackHostname: string): ParsedUrl {
  const value = (inputUrl || '').trim();
  const host = normalizeHost(fallbackHostname || window.location.hostname || '');
  const looksLikeHost = /^[^/?#]+\.[^/?#]+/.test(value);
  const normalizedValue = /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(value) || value.startsWith('/') || !looksLikeHost
    ? value
    : `https://${value}`;

  try {
    const parsed = new URL(normalizedValue);
    return {
      hostname: normalizeHost(parsed.hostname),
      pathname: parsed.pathname || '',
      parameters: parsed.search || '',
    };
  } catch (e) {
    try {
      const parsed = new URL(value.startsWith('/') ? value : `/${value}`, `https://${host || 'example.com'}`);
      return {
        hostname: host,
        pathname: parsed.pathname || '',
        parameters: parsed.search || '',
      };
    } catch (err) {
      return {
        hostname: host,
        pathname: value,
        parameters: '',
      };
    }
  }
}

function matchUrlPart(pathname: string, hostname: string, parameters: string, url: string): boolean {
  let urlValue = normalizeRuleUrl(url);
  if (urlValue) {
    urlValue = replaceAllText(urlValue, '?', '\\?');
  }

  let result = '';
  let hasOther = false;
  for (let i = 0; i < urlValue.length; i++) {
    const chart = urlValue[i];
    if (chart === '*' && !hasOther) {
      continue;
    }
    hasOther = true;
    result += chart;
  }

  const reg = new RegExp(`(?:.*)${result}(?:.*)`);
  if (hostname) {
    return reg.test(hostname + pathname + parameters);
  }
  return reg.test(pathname);
}

function matchExactUrl(pathname: string, hostname: string, parameters: string, urlItem: TargetingUrlRule): boolean {
  const sourceUrl = normalizeRuleUrl(getRuleText(urlItem));
  const dontHasSearch = !sourceUrl.includes('?');

  if (!(typeof urlItem !== 'string' && urlItem.completeMatching) || dontHasSearch) {
    if (hostname.length === 0) {
      return pathname === sourceUrl;
    }

    let urlArr = sourceUrl.split('');
    const searchIndex = urlArr.findIndex((item) => item === '?');
    if (searchIndex !== -1) {
      urlArr = urlArr.map((item, index) => (index < searchIndex ? item : ''));
    }

    let normalizedUrl = urlArr.join('');
    const slashCount = (urlArr.filter((item) => item === '/') || []).length;
    if (pathname === '/' && !normalizedUrl.includes('/')) {
      normalizedUrl += '/';
    } else if (pathname === '' && slashCount === 0 && urlArr[urlArr.length - 1] === '/') {
      normalizedUrl = urlArr.filter((item) => item !== '/').join('');
    }

    if (normalizedUrl.length > 0) {
      if (normalizedUrl[0] !== '*') {
        normalizedUrl = `^${normalizedUrl}`;
      }
      if (normalizedUrl[normalizedUrl.length - 1] !== '*') {
        normalizedUrl = `${normalizedUrl}$`;
      }
    }

    const reg = new RegExp(replaceAllText(normalizedUrl, '*', '(?:.*)'));
    return reg.test(hostname + pathname);
  }

  return matchUrlPart(pathname, hostname, parameters, sourceUrl);
}

function matchRuleList(
  pathname: string,
  hostname: string,
  parameters: string,
  urls: TargetingUrlRule[] | undefined,
  matcher: (pathname: string, hostname: string, parameters: string, urlItem: TargetingUrlRule) => boolean
): TargetingUrlRule | null {
  const list = urls || [];
  for (let i = 0; i < list.length; i++) {
    if (matcher(pathname, hostname, parameters, list[i])) {
      return list[i];
    }
  }
  return null;
}

function matchPartRule(pathname: string, hostname: string, parameters: string, urlItem: TargetingUrlRule): boolean {
  return matchUrlPart(pathname, hostname, parameters, getRuleText(urlItem));
}

// 导出匹配函数，签名与 C 端 react-plugin/src/service/strategy/logic.ts 对齐，
// 用于 B 端单元测试，确保两端 URL 圈选逻辑行为一致。
export function matchExactUrls(
  pathname: string,
  hostname: string,
  parameters: string,
  urls: TargetingUrlRule[]
): boolean {
  return matchRuleList(pathname, hostname, parameters, urls, matchExactUrl) !== null;
}

export function matchUrlParts(
  pathname: string,
  hostname: string,
  parameters: string,
  urls: TargetingUrlRule[]
): boolean {
  return matchRuleList(pathname, hostname, parameters, urls, matchPartRule) !== null;
}

function buildPageRules(pages: Array<string | number> | undefined, hostname: string): PageRuleGroup {
  const exactUrls: TargetingUrlRule[] = [];
  const includeUrls: TargetingUrlRule[] = [];
  const pageIds = normalizePageIds(pages);
  const host = normalizeHost(hostname);

  pageIds.forEach((page) => {
    if (page === '1' && host) {
      exactUrls.push({ url: host });
    } else if (page === '2') {
      includeUrls.push({ url: '*/collection/*' }, { url: '*/collections/*' });
    } else if (page === '3') {
      includeUrls.push({ url: '*/product/*' }, { url: '*/products/*' });
    } else if (page === '4') {
      includeUrls.push({ url: '*/cart/*' }, { url: '*/carts/*' });
    } else if (page === '5') {
      includeUrls.push({ url: '*/checkout/*' }, { url: '*/checkouts/*' });
    }
  });

  return { exactUrls, includeUrls };
}

function getRuleText(rule: TargetingUrlRule | null): string {
  if (!rule) return '';
  if (typeof rule === 'string') return rule;
  return rule.url || '';
}

function hasIncludeTargeting(targeting: TargetingPreviewData): boolean {
  return Boolean(
    (targeting.urls && targeting.urls.length > 0) ||
      (targeting.urlParts && targeting.urlParts.length > 0) ||
      (targeting.pages && targeting.pages.length > 0)
  );
}

function getPageNames(pages?: Array<string | number>): string {
  return normalizePageIds(pages)
    .map((page) => pageName(page))
    .join(tx(null, 'common.enumJoin', '、'));
}

function getAllRuleTexts(targeting: TargetingPreviewData): string[] {
  return [
    ...(targeting.urls || []),
    ...(targeting.urlParts || []),
    ...(targeting.excludeUrls || []),
    ...(targeting.excludeUrlParts || []),
  ]
    .map((rule) => getRuleText(rule))
    .filter(Boolean);
}

function materializeRuleUrl(ruleUrl: string, host: string): string | null {
  const value = (ruleUrl || '').trim();
  if (!value) return null;

  let url = value;
  if (/^https?:\/\//.test(url)) {
    return url.replace(/\*/g, 'example');
  }
  if (url.startsWith('*/')) {
    url = `https://${host}${url.slice(1)}`;
  } else if (url.startsWith('/')) {
    url = `https://${host}${url}`;
  } else if (url.startsWith('*')) {
    url = `https://${host}${url.replace(/^\*+/, '')}`;
  } else if (normalizeRuleUrl(url).startsWith(host)) {
    url = `https://${url}`;
  } else {
    return null;
  }

  return url.replace(/\*/g, 'example');
}

function getPageTypeByUrl(url: string, host: string): string {
  const parsed = parsePreviewUrl(url, host);
  const pathname = parsed.pathname || '';
  if (pathname === '/' || pathname === '') return 'home';
  if (/\/collections?\//.test(pathname)) return 'collection';
  if (/\/products?\//.test(pathname)) return 'product';
  if (/\/carts?(\/|$)/.test(pathname)) return 'cart';
  if (/\/checkouts?(\/|$)/.test(pathname)) return 'checkout';
  return '';
}

function getConfiguredUrlsForPage(targeting: TargetingPreviewData, host: string, pageType: string): string[] {
  return getAllRuleTexts(targeting)
    .map((ruleUrl) => materializeRuleUrl(ruleUrl, host))
    .filter((url): url is string => Boolean(url))
    .filter((url) => getPageTypeByUrl(url, host) === pageType);
}

// B 端预检需与 C 端 react-plugin/src/service/strategy/logic.ts 的 URL 命中顺序保持一致。
export function previewTargetingUrl(
  targeting: TargetingPreviewData,
  inputUrl: string,
  fallbackHostname: string
): TargetingPreviewResult {
  const parsed = parsePreviewUrl(inputUrl, fallbackHostname);
  const { hostname, pathname, parameters } = parsed;

  // 展示规则判断顺序：先排除，再判断展示范围。
  const excludeExactRule = matchRuleList(pathname, hostname, parameters, targeting.excludeUrls, matchExactUrl);
  if (excludeExactRule) {
    return {
      url: inputUrl,
      visible: false,
      reason: tx(null, 'form.rules.preview.hitExcludeExact', '命中排除规则：高级 URL 等于'),
      matchedRule: getRuleText(excludeExactRule),
    };
  }

  const excludePartRule = matchRuleList(pathname, hostname, parameters, targeting.excludeUrlParts, matchPartRule);
  if (excludePartRule) {
    return {
      url: inputUrl,
      visible: false,
      reason: tx(null, 'form.rules.preview.hitExcludePart', '命中排除规则：高级 URL 包含'),
      matchedRule: getRuleText(excludePartRule),
    };
  }

  const excludePageRules = buildPageRules(targeting.excludePages, fallbackHostname);
  if (
    matchRuleList(pathname, hostname, parameters, excludePageRules.exactUrls, matchExactUrl) ||
    matchRuleList(pathname, hostname, parameters, excludePageRules.includeUrls, matchPartRule)
  ) {
    return {
      url: inputUrl,
      visible: false,
      reason: tx(null, 'form.rules.preview.hitExcludePages', '命中排除规则：{{pages}}', { pages: getPageNames(targeting.excludePages) }),
      matchedRule: getPageNames(targeting.excludePages),
    };
  }

  const includeExactRule = matchRuleList(pathname, hostname, parameters, targeting.urls, matchExactUrl);
  if (includeExactRule) {
    return {
      url: inputUrl,
      visible: true,
      reason: tx(null, 'form.rules.preview.hitIncludeExact', '命中展示规则：高级 URL 等于'),
      matchedRule: getRuleText(includeExactRule),
    };
  }

  const includePartRule = matchRuleList(pathname, hostname, parameters, targeting.urlParts, matchPartRule);
  if (includePartRule) {
    return {
      url: inputUrl,
      visible: true,
      reason: tx(null, 'form.rules.preview.hitIncludePart', '命中展示规则：高级 URL 包含'),
      matchedRule: getRuleText(includePartRule),
    };
  }

  const includePageRules = buildPageRules(targeting.pages, fallbackHostname);
  if (
    matchRuleList(pathname, hostname, parameters, includePageRules.exactUrls, matchExactUrl) ||
    matchRuleList(pathname, hostname, parameters, includePageRules.includeUrls, matchPartRule)
  ) {
    return {
      url: inputUrl,
      visible: true,
      reason: tx(null, 'form.rules.preview.hitIncludePages', '命中展示规则：{{pages}}', { pages: getPageNames(targeting.pages) }),
      matchedRule: getPageNames(targeting.pages),
    };
  }

  if (!hasIncludeTargeting(targeting)) {
    return {
      url: inputUrl,
      visible: true,
      reason: tx(null, 'form.rules.preview.defaultAll', '未配置展示范围，默认全站展示'),
      matchedRule: tx(null, 'form.rules.preview.matchedAll', '全站默认展示'),
    };
  }

  return {
    url: inputUrl,
    visible: false,
    reason: tx(null, 'form.rules.preview.noMatch', '未命中任何展示规则'),
    matchedRule: '-',
  };
}

export function buildTargetingExamples(targeting: TargetingPreviewData, hostname: string): TargetingPreviewExampleRow[] {
  const host = normalizeHost(hostname || window.location.hostname || 'example.com') || 'example.com';
  const examples = [
    { pageName: pageName('1'), pageType: 'home', url: `https://${host}/` },
    {
      pageName: pageName('2'),
      pageType: 'collection',
      url: `https://${host}/collections/example`,
      displayUrl: `https://${host}/collection/example\nhttps://${host}/collections/example`
    },
    {
      pageName: pageName('3'),
      pageType: 'product',
      url: `https://${host}/products/example`,
      displayUrl: `https://${host}/product/example\nhttps://${host}/products/example`
    },
    {
      pageName: pageName('4'),
      pageType: 'cart',
      url: `https://${host}/cart`,
      displayUrl: `https://${host}/cart\nhttps://${host}/carts`
    },
    {
      pageName: pageName('5'),
      pageType: 'checkout',
      url: `https://${host}/checkout`,
      displayUrl: `https://${host}/checkout\nhttps://${host}/checkouts`
    },
  ];

  return examples.map((item) => {
    const urls = Array.from(new Set([item.url, ...getConfiguredUrlsForPage(targeting, host, item.pageType)]));
    const details = urls.map((url) => ({
      ...previewTargetingUrl(targeting, url, host),
      pageName: item.pageName,
    }));
    const visibleCount = details.filter((detail) => detail.visible).length;
    const showStatus = visibleCount === details.length ? '展示' : visibleCount === 0 ? '不展示' : '部分展示';
    const firstHidden = details.find((detail) => !detail.visible);
    const firstVisible = details.find((detail) => detail.visible);

    return {
      pageName: item.pageName,
      url: item.url,
      visible: showStatus !== '不展示',
      reason: showStatus === '部分展示' ? tx(null, 'form.rules.preview.partialHit', '部分 URL 命中不展示规则') : (firstHidden || firstVisible)?.reason || '',
      matchedRule: (firstHidden || firstVisible)?.matchedRule || '',
      showStatus,
      details,
      displayUrl: item.displayUrl || item.url,
    };
  });
}
